import api from "./api";
import axios from "axios";
import { store } from "../store/store";
import { alertService } from "./alertService";
import { banks } from "../interfaces/BankInterface";
import { UserInterface } from "../interfaces/UserInterface";
import { currencyExchange, CurrencyExchanges } from "../utils";
import { DailyOperationStatus } from "../interfaces/Dtos/DailyStoreOperationDTO";
import { DocumentReplacementDTO } from "../interfaces/Dtos/DocumentReplacementDTO";
import {
  DocumentDto,
  PaymentExtendedDto,
  ShipmentItemAmountDto,
  ShipmentSimpleResponseDto,
} from "../interfaces/Dtos";
import {
  Nullable,
  DocumentStatus,
  PaymentInterface,
  DocumentInterface,
  DocumentItemInterface,
  ResponseInterface,
  BusinessUnitInterface,
  ShipmentSimpleInterface,
  DocumentType,
  PaymentMethodEnum,
  TaxInterface,
  AccountInterface,
  StoreDocumentInterface,
  ShipmentInterface,
  ItemInterface,
  ShipmentService,
} from "../interfaces";
import { getAccount } from "./accountServices";
import { getShipment } from "./shipmentServices";
import { updateFileDO, uploadFileDO } from "./digitaloceanServices";
import {
  validateBillingAccount,
  MISSING_FISCAL_ADDRESS_ERROR,
} from "../utils/billingValidations";

const baseURLAPI = process.env.REACT_APP_API_HOST;

// Method to get Document by its identifiers
export const getDocument = async (
  documentId?: string,
  documentNumber?: string
): Promise<Nullable<DocumentInterface>> => {
  let uri = `${baseURLAPI}/Document`;
  if (documentId) {
    uri = `${uri}?DocumentID=${documentId}`;
  } else if (documentNumber) {
    uri = `${uri}?DocumentNumber=${documentNumber}`;
  } else {
    return null;
  }
  try {
    const response = await api.get(uri);

    if (response.status === 200 && response.data.model != null) {
      const dto: DocumentDto = response.data.model;
      if (dto == null) return null;
      const document: DocumentInterface =
        mapDocumentDtoToDocumentInterface(dto);
      return document;
    }
  } catch (error) {
    const message = (error as any)?.response?.data?.errorMessage;
    alertService.error("No se pudo obtener el documento", message);
  }

  return null;
};

/**
 * Fills in what getDocument's mapping leaves out: the guías as full
 * ShipmentInterface objects and the owner as a full account. Shared by the
 * /documents/:id route, by its Pagar button and by the row of the listing,
 * which used to carry three copies of the same loop.
 *
 * Why the guías are read at all: the mapping below builds
 * ShipmentSimpleInterface rows that carry no `items`, and
 * setShipmentCreateDocument copies document.shipments straight into the
 * shipmentCreate state, where the subtotal and the VAT are computed by
 * iterating `items`. Without them the payment screen throws on render.
 *
 * The reads stay serial, one guía at a time, exactly as the three loops did.
 * A corporate invoice can hold 500 of them and that is the request storm this
 * screen is known for, but firing them in parallel would not remove a single
 * request: it would only make the burst sharper, and the shared axios
 * instance renews the session on each 401 with no mutex. Removing the reads
 * is separate work that needs the guía fields to travel inside the document.
 * What this does save is a number that repeats: it is asked for once.
 *
 * Returns null unless every guía came back. A partial document is worse than
 * a failure: getShipment swallows every error into null (network, 5xx, a
 * mapping that blew up), so three guías failing out of 500 used to produce a
 * document of 497 with no error anywhere, and the payment screen would then
 * compute the subtotal and the VAT over those 497 and charge them. Refuse
 * instead, and let the caller say so.
 */
export const hydrateDocument = async (
  document: DocumentInterface
): Promise<Nullable<DocumentInterface>> => {
  // The two array types behind `shipments` share no call signature, so the
  // union has no callable map(): hence the cast to the one field read here.
  const rows = document.shipments as { shipmentNumber?: string }[];

  const byNumber = new Map<string, ShipmentInterface>();
  for (const row of rows) {
    // An empty number would reach the API as ?ShipmentNumber= and answer 500:
    // the server parses it as a number and then as a GUID, and neither is
    // guarded. It is left unresolved, which fails the check below.
    if (!row.shipmentNumber || byNumber.has(row.shipmentNumber)) continue;
    const shipment = await getShipment(row.shipmentNumber);
    if (shipment) byNumber.set(row.shipmentNumber, shipment);
  }

  // Rebuilt by number and not by counting, so a document that repeats a guía
  // still resolves and one that lost a guía still fails.
  const shipments = rows.map((row) =>
    row.shipmentNumber ? byNumber.get(row.shipmentNumber) : undefined
  );
  if (shipments.some((shipment) => !shipment)) return null;

  const accountOwner =
    (await getAccount(document.accountOwner?.id ?? "")) ?? document.accountOwner;

  return {
    ...document,
    accountOwner,
    shipments: shipments as ShipmentInterface[],
  };
};

/**
 * Builds one light guía row of an invoice from its DTO.
 *
 * Every presentation key is created only when the API carried a value for it.
 * That is not tidiness: the invoice table guards with key presence and not
 * with value, so `"location" in shipment.buSource` is true even when the value
 * is null, and Origen would then print the literal text "CCS - undefined"
 * where today it prints "CCS". Until [document].[GetDocumentById] projects the
 * new columns they all arrive null and this produces exactly the rows the
 * screen renders today.
 */
export const mapShipmentSimpleResponseDtoToShipmentSimpleInterface = (
  row: ShipmentSimpleResponseDto
): ShipmentSimpleInterface => {
  // Joined and not concatenated: for a row with both lines this is the same
  // string the hydrated guía composes, which is what lets the table switch
  // from hydrated rows to light ones without a visual change. For a row
  // missing the first line it differs on purpose, and to the good: the
  // hydrated path prints the word "null" there, and plain concatenation would
  // leave a leading separator.
  const address = [row.consigneeAddressLine1, row.consigneeAddressLine2]
    .filter((line): line is string => !!line)
    .join(". ");

  return {
    id: row.shipmentHeaderID,
    shipmentNumber: row.shipmentNumber,
    paymentMode: row.paymentModeID,
    shipper: { accountFullName: row.shipperName },
    consignee: { accountFullName: row.consigneeName },
    buSource: row.sourceBUAreaName
      ? { code: row.buCodeSource, location: { name: row.sourceBUAreaName } }
      : { code: row.buCodeSource },
    buConsignee: row.consigneeBUAreaName
      ? {
          code: row.buCodeConsignee,
          location: { name: row.consigneeBUAreaName },
        }
      : { code: row.buCodeConsignee },
    consigneeAddress: address
      ? {
          addressName: row.consigneeAddressName ?? row.consigneeAddress,
          address,
          // "" and not absent: the table guards the postal code by value as
          // well as by key, so an empty one prints nothing, which is what it
          // does today.
          postalCode: row.consigneePostalCode ?? "",
        }
      : { addressName: row.consigneeAddressName ?? row.consigneeAddress },
    shipperAddress: { addressName: row.shipperAddress },
    status: row.statusID,
    total: +row.totalAmountBaseCurr.toFixed(2),
    // 0 is the server's value for "unknown", so it must not become a key: the
    // table would then compare it against the two international services and
    // find neither, which is the same outcome as absent, but the key would
    // travel into the payment state as a real service.
    ...(row.serviceID ? { service: row.serviceID as ShipmentService } : {}),
  };
};

/**
 * Upper bound the server accepts in one call, mirrored from
 * ShipmentItemsByNumberRequest.MaxShipmentNumbers. Over it the request is
 * refused with a 400, so the client splits instead: a payment that will not
 * start and cannot say why is worse than two requests.
 */
const MAX_SHIPMENT_NUMBERS_PER_CALL = 1000;

/**
 * Reads the items of many guías in one call, for the payment of an invoice.
 *
 * Paying used to need every guía hydrated, one request each, because the
 * subtotal and the VAT are computed by iterating `items`. A corporate invoice
 * of 500 guías is 500 requests and 500 executions of the heaviest stored
 * procedure in the API. This is the batched read behind them: the numbers
 * travel in the body and not in the query string because 500 of them exceed
 * the 2048 byte default that request filtering allows in a URL.
 *
 * The server trims, drops blanks and deduplicates the list, refuses an empty
 * one, and caps it. It answers the same envelope as the rest of the API, with
 * one flat row per item carrying the number of the guía it belongs to. A SQL
 * failure arrives as a 200 with didError set, so the caller must read the
 * envelope and never the status code.
 */
export const getShipmentItemsByNumbers = async (
  shipmentNumbers: string[]
): Promise<ResponseInterface<ShipmentItemAmountDto[]>> => {
  const uri = `${baseURLAPI}/Document/ShipmentItems`;
  const blocks: string[][] = [];
  for (
    let index = 0;
    index < shipmentNumbers.length;
    index += MAX_SHIPMENT_NUMBERS_PER_CALL
  ) {
    blocks.push(
      shipmentNumbers.slice(index, index + MAX_SHIPMENT_NUMBERS_PER_CALL)
    );
  }

  const rows: ShipmentItemAmountDto[] = [];
  // Serial, like every other read on this screen. Splitting is a fallback for
  // an invoice larger than any seen so far, not a place to open connections.
  for (const block of blocks) {
    const body = JSON.stringify({ ShipmentNumberList: block });

    let answer: ResponseInterface<ShipmentItemAmountDto[]>;
    try {
      const response = await api.post(uri, body, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      answer = response.data;
    } catch (error) {
      // The house pattern returns the server envelope and nothing else, which
      // is undefined when the request never reached the server. A caller that
      // only checks didError would then read a failure as an empty success and
      // charge an invoice with no items. Always answer a well formed envelope.
      answer = (error as any)?.response?.data ?? {
        message: "",
        didError: true,
        errorMessage: "No se pudieron obtener los artículos de las guías.",
        model: null,
      };
    }

    // One failed block fails the read. Returning what the other blocks brought
    // would be the partial invoice this plan exists to stop.
    if (!answer || answer.didError || !answer.model) return answer;
    rows.push(...answer.model);
  }

  return { message: "", didError: false, errorMessage: "", model: rows };
};

/**
 * Groups the flat rows the batched read answers by guía number, and tells the
 * caller whether every number it asked for came back.
 *
 * A guía missing from the answer is not the same as a guía with no items, and
 * the two are indistinguishable in the flat list. Paying an invoice whose
 * items are incomplete charges a subtotal that is not the invoice's, so the
 * caller has to be able to refuse. `missing` is that signal.
 */
export const groupShipmentItemsByNumber = (
  shipmentNumbers: string[],
  rows: ShipmentItemAmountDto[]
): {
  itemsByNumber: Map<string, ShipmentItemAmountDto[]>;
  missing: string[];
} => {
  const itemsByNumber = new Map<string, ShipmentItemAmountDto[]>();

  for (const row of rows) {
    if (!row?.shipmentNumber) continue;
    const items = itemsByNumber.get(row.shipmentNumber);
    if (items) items.push(row);
    else itemsByNumber.set(row.shipmentNumber, [row]);
  }

  const missing = shipmentNumbers.filter(
    (shipmentNumber) => !itemsByNumber.has(shipmentNumber)
  );

  return { itemsByNumber, missing };
};

/**
 * Which pair of amount columns the batched read answers with. The rows carry
 * both a plain and a BaseCurr variant of every figure, and picking the wrong
 * one shifts every total by the exchange rate without failing anything, which
 * is the one mistake in this whole change that moves money.
 *
 * So it is not guessed. Both pairs are tried and the one that reproduces the
 * subtotal the server already sent wins. Collapse this to a single pair once
 * QA has confirmed which it is against a real invoice.
 */
const AMOUNT_PAIRS = [
  { total: "totalAmountBaseCurr", tax: "taxAmountBaseCurr" },
  { total: "totalAmount", tax: "taxAmount" },
] as const;

/** Cents of slack, to absorb the float noise of summing many rows. */
const SUBTOTAL_TOLERANCE = 0.01;

/**
 * Attaches the batched items to the light guía rows of an invoice, producing
 * the guías the payment screen computes its subtotal and its VAT over.
 *
 * Returns null when the result cannot be trusted: a guía whose items did not
 * come back, or a total that does not reconcile against `document.subTotal`,
 * which the server computed. The caller then falls back to reading guía by
 * guía, which is slow but known to be right.
 */
export const attachItemsToDocumentShipments = (
  document: DocumentInterface,
  rows: ShipmentItemAmountDto[]
): Nullable<ShipmentInterface[]> => {
  const lightRows = document.shipments as ShipmentSimpleInterface[];
  const shipmentNumbers = lightRows.map((row) => row.shipmentNumber);
  const { itemsByNumber, missing } = groupShipmentItemsByNumber(
    shipmentNumbers,
    rows
  );

  // A guía with no items and a guía that never came back look the same in a
  // flat list, and charging an invoice that is missing one charges a subtotal
  // that is not the invoice's.
  if (missing.length > 0) return null;

  for (const pair of AMOUNT_PAIRS) {
    const shipments = lightRows.map((row) => ({
      ...row,
      items: (itemsByNumber.get(row.shipmentNumber) ?? []).map(
        (item, index) => ({
          id: item.itemID.toString(),
          // The batched row carries no item code and nothing on the payment
          // screen reads one.
          code: "",
          name: item.itemName,
          order: index,
          mandatory: true,
          isItemBase: item.isItemBase,
          rate: {
            // Always the total, never the discount percentage: the hydrated
            // guía only prefers the percentage for a document it does not
            // have, and here there is always a document.
            value: item[pair.total] ?? 0,
            isPercentage: item.itemID === 31 && !item[pair.total],
            iva: item[pair.tax] ?? 0,
            distance: 0,
            // Hardcoded to zero in the hydrated mapping too: the Ipostel memo
            // never depended on the items.
            ipostel: 0,
          },
        })
      ),
    })) as unknown as ShipmentInterface[];

    const subTotal = shipments.reduce(
      (acc, shipment) =>
        acc + shipment.items.reduce((sum, item) => sum + item.rate.value, 0),
      0
    );

    if (Math.abs(subTotal - document.subTotal) <= SUBTOTAL_TOLERANCE) {
      return shipments;
    }
  }

  return null;
};

/**
 * The guías a payment needs, read in one request instead of one per guía.
 *
 * An invoice of 500 guías used to cost 501 requests here and now costs two,
 * the batched items and the owner account. Falls back to the per-guía read
 * whenever the batch cannot be trusted, so the worst case is what this screen
 * did before and never a wrong amount.
 */
export const getDocumentShipmentsForPayment = async (
  document: DocumentInterface
): Promise<Nullable<DocumentInterface>> => {
  const shipmentNumbers = (document.shipments as ShipmentSimpleInterface[])
    .map((row) => row.shipmentNumber)
    .filter((shipmentNumber) => !!shipmentNumber);

  if (shipmentNumbers.length > 0) {
    const response = await getShipmentItemsByNumbers(shipmentNumbers);

    if (!response?.didError && response?.model) {
      const shipments = attachItemsToDocumentShipments(document, response.model);

      if (shipments) {
        const accountOwner =
          (await getAccount(document.accountOwner?.id ?? "")) ??
          document.accountOwner;

        return { ...document, accountOwner, shipments };
      }
    }
  }

  return hydrateDocument(document);
};

// Map object from DB to more legible interfaces
export const mapDocumentDtoToDocumentInterface = (
  dto: DocumentDto
): DocumentInterface => {
  const currencyList = store.getState().inmutable.currencyList;
  const invoiceTaxes = [
    {
      name: "IVA",
      value: dto.taxAmountBaseCurr,
      type: "fixed",
    },
    {
      name: "Ipostel",
      value: dto.postalTaxAmount ?? 0,
      type: "fixed",
    },
  ] as TaxInterface[];

  if (dto.igtfTaxAmount > 0) {
    invoiceTaxes.push({
      name: "IGTF",
      value: dto.igtfTaxAmount,
      type: "fixed",
    });
  }

  let creationDate: Date;
  try {
    creationDate = new Date(dto.creationDate);
  } catch (error) {
    creationDate = new Date();
  }

  const document: DocumentInterface = {
    documentID: dto.documentID,
    documentNumber: dto.documentNumber,
    documentType: dto.documentTypeID,
    documentTypeName: dto.documentTypeName,
    documentTypeCode: dto.documentTypeCode,
    documentAffectedID: dto.documentAffectedID,
    documentAffectedDocumentNumber: dto.documentAffectedDocumentNumber,
    documentAppliedID: dto.documentAppliedID,
    documentAppliedDocumentNumber: dto.documentAppliedDocumentNumber,
    urlDocument: dto.urlDocument,
    accountOwner: dto.account as AccountInterface,
    paymentMode:
      !!dto.shipments && dto.shipments.length > 0
        ? dto.shipments[0].paymentModeID
        : 0,
    subTotal: dto.grossAmountBaseCurr,
    total: dto.totalAmountBaseCurr,
    status: dto.statusID,
    hasRetention: dto.hasRetention,
    balanceAmount: dto.balanceAmountBaseCurr,
    baseBalanceAmount: +dto.balanceAmountBaseCurr.toFixed(2),
    igtfAmount: dto.igtfTaxAmount,
    buCodeInvoicer: dto.buCodeInvoicer,
    fiscalControlNumber: dto.fiscalControlNumber ?? undefined,
    shipments: dto.shipments.map(
      mapShipmentSimpleResponseDtoToShipmentSimpleInterface
    ),
    payments: dto.payments.map(
      (payment: PaymentExtendedDto) =>
        ({
          paymentID: payment.paymentID,
          paymentDetailID: payment.paymentDetailID,
          proofOfPayment: payment.attachmentID,
          amount: payment.paymentAmountBaseCurr,
          sourceBankID: payment.sourceBankID,
          destBankID: payment.bankAccountID,
          paymentMethod: {
            paymentMethodID: payment.paymentMethodID,
            paymentMethodName: payment.paymentMethodName,
            currencyID: payment.currencyID,
            bankID: payment.bankAccountID,
            taxes: [],
          },
          igtfAmount: 0,
          igtfPercentage: 0,
          reference: payment.referenceNum,
          currency: {
            id: payment.currencyID,
            name:
              currencyList.find((pay) => pay.currencyID === payment.currencyID)
                ?.currencyName ?? "",
            code:
              currencyList.find((pay) => pay.currencyID === payment.currencyID)
                ?.currencyIsoCode ?? "",
          },
          status: payment.statusID,
          creationDate: payment.creationDate,
          paymentDate: payment.paymentDetailDate,
          isRetention: [
            PaymentMethodEnum.RETENCION_DE_IMPUESTO_MUNICIPAL,
            PaymentMethodEnum.RETENCION_DE_ISRL,
            PaymentMethodEnum.RETENCION_DE_IVA,
          ].includes(payment.paymentMethodID), // check if inside a list of retentions
        } as PaymentInterface)
    ),
    taxes: invoiceTaxes,
    printed: false,
    creationDate: creationDate,
    documentDate: dto.documentDate ? new Date(dto.documentDate) : undefined,
    //updateDate: dto.CreationDate,
  };
  return document;
};

// Method to obtain the Documents by the dynamic string search and filtering by status and type
export const documentDynamicSearch = async (
  search?: string,
  buCodeSource?: string,
  buCodeConsignee?: string,
  documentTypes?: DocumentType[],
  statuses?: DocumentStatus[]
): Promise<Nullable<DocumentInterface[]>> => {
  const documentTypeIDList = documentTypes?.join(",");
  //Force filter for 4 statuses: Pending, Partial Paid, Paid, Anulled
  if (!statuses) statuses = [11, 20, 10, 6];
  const StatusIDList = statuses?.join(",");

  let uri = `${baseURLAPI}/Document/DynamicSearch?`;
  if (search) {
    uri += `Search=${search}&`;
  }
  if (buCodeSource) {
    uri += `BuCodeSource=${buCodeSource}&`;
  }
  if (buCodeConsignee) {
    uri += `BuCodeConsignee=${buCodeConsignee}&`;
  }
  if (documentTypeIDList) {
    uri += `DocumentTypeIDList=${documentTypeIDList}&`;
  }
  if (StatusIDList) {
    uri += `StatusIDList=${StatusIDList}&`;
  }
  try {
    const response = await api.get(uri);

    if (response.status === 200 && response.data.model != null) {
      const dtoList: DocumentDto[] = response.data.model;
      if (!dtoList) return null;
      return dtoList.map((dto) => mapDocumentDtoToDocumentInterface(dto));
    }
  } catch (error) {
    alertService.error("No se pudo obtener el documento");
  }

  return null;
};

// Get All documents Order  and Factura by BUConsignee
export const getDocumentsByBUConsignee = async (
  businessUnit?: BusinessUnitInterface
): Promise<Nullable<DocumentInterface[]>> => {
  return await documentDynamicSearch(undefined, undefined, businessUnit?.code, [
    DocumentType.ORDER,
    DocumentType.INVOICE,
  ]);
};

const saveNationalDocument = async (
  document: DocumentInterface,
  buCode?: string,
  user?: UserInterface,
  accountBillTo?: AccountInterface,
  observations?: string,
  applicationID?: number
): Promise<ResponseInterface<{ id_solicitud: string; documentID: string }>> => {
  const shipments = document.shipments as ShipmentInterface[];

  /**
   * Un solo titular para todo el payload.
   *
   * Antes el ID salía de `document.accountOwner` y el nombre, correo y dirección
   * de `accountBillTo ?? shipments[0].accountBillTo`. Si esas fuentes divergían se
   * validaba la dirección de una cuenta y se facturaba a otra. Los dos únicos
   * llamadores (`ShipmentCreate.onSave`, `Pickup.onSave`) pasan el mismo objeto por
   * ambas vías, así que unificar no cambia lo que se factura hoy — cierra la
   * posibilidad de que un tercer llamador las separe.
   *
   * El parámetro explícito manda: se llama `accountBillTo` y existe justamente para
   * eso. `shipments[0].accountBillTo` queda de último recurso y **nunca puede pasar
   * la guarda**: ApiCore no incluye la dirección fiscal en el bloque `AccountBillTo`
   * del envío (no existe el campo en su DTO), así que ese objeto llega siempre sin
   * ella. Es correcto que bloquee: no tenemos el dato que la ley exige.
   */
  const owner =
    accountBillTo ?? document.accountOwner ?? shipments[0].accountBillTo;
  const paymentMode = shipments[0].paymentMode;
  const accountSiteID = shipments[0].accountSiteID;

  const billingCheck = validateBillingAccount(owner);
  if (billingCheck.error) {
    return {
      message: "",
      model: null,
      didError: true,
      errorMessage: billingCheck.message,
    };
  }

  const uri = `${baseURLAPI}/Document`;
  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const body = JSON.stringify({
    Document: {
      Items: [],
      PctOtherTax: "0",
      DocumentTypeID: "6",
      PctMunicipalTax: "0",
      Observations: observations,
      CreationUser: user?.userLogin,
      BUCode: buCode,
      PaymentModeID: paymentMode,
      AccountBillToID: owner?.id,
      AccountSiteID: accountSiteID,
      AccountPhone: owner?.fiscalPhoneNumber,
      AccountName: owner?.accountFullName,
      AccountEmail: owner?.listAccountEmail[0]?.email,
      // Tercer dato del numeral 7. Sin él ApiCore deja el número del comprador en
      // un guion suelto (`NormalizeIdentification`), y así sale impreso.
      AccountFiscalNumber: owner?.identificationNumber,
      AccountLegalAddress: owner?.fiscalAddress,
      HasRetention: document.hasRetention,
      ShipmentNumbers: shipments.map((s) => s.shipmentNumber),
      StatusID: document.status,
      ApplicationID: applicationID,
    },
  });

  try {
    const response = await api.post(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });
    if (response.status === 201 || response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return (error as any)?.response?.data;
  }

  return {
    message: "",
    model: null,
    didError: true,
    errorMessage: "Hubo un error al guardar el documento.",
  };
};

const saveInternationalDocument = async (
  document: DocumentInterface,
  exchanges: CurrencyExchanges,
  buCode?: string,
  user?: UserInterface,
  accountBillTo?: AccountInterface,
  observations?: string,
  exchangeRate?: number
): Promise<ResponseInterface<{ id_solicitud: string; documentID: string }>> => {
  const shipments = document.shipments as ShipmentInterface[];
  // Misma resolución y misma guarda que en la ruta nacional; ver el comentario
  // largo en `saveNationalDocument`.
  const owner =
    accountBillTo ?? document.accountOwner ?? shipments[0].accountBillTo;
  const paymentMode = shipments[0].paymentMode;
  const accountSiteID = shipments[0].accountSiteID;

  const billingCheck = validateBillingAccount(owner);
  if (billingCheck.error) {
    return {
      message: "",
      model: null,
      didError: true,
      errorMessage: billingCheck.message,
    };
  }

  const allShipmentItems = shipments.reduce(
    (list, shipment) => list.concat(shipment.items),
    [] as ItemInterface[]
  );

  const grossAmountBaseCurr = allShipmentItems.reduce(
    (acc, item) => acc + item.rate.value,
    0
  );
  const taxAmountBaseCurr = allShipmentItems.reduce(
    (acc, item) => acc + item.rate.iva,
    0
  );
  const postalTaxAmountBaseCurr = allShipmentItems.reduce(
    (acc, item) => acc + (item.rate.ipostel ?? 0),
    0
  );
  const taxExemptAmountBaseCurr = allShipmentItems.reduce(
    (acc, item) => (item.rate.iva === 0 ? acc + item.rate.value : acc),
    0
  );
  const taxNoExemptAmountBaseCurr = allShipmentItems.reduce(
    (acc, item) => (item.rate.iva !== 0 ? acc + item.rate.value : acc),
    0
  );

  // Group all shipment items by item id
  let itemSeqNumber = 1;
  const documentItems: DocumentItemInterface[] = [];
  allShipmentItems
    .reduce((acc, item) => {
      const documentItem = acc.get(item.id);

      if (!documentItem) {
        acc.set(item.id, {
          ItemID: item.id,
          ItemSeqNumber: itemSeqNumber++,
          Quantity: 1,
          ItemName: item.name,
          GrossAmount: item.rate.valueAmount ?? 0,
          GrossAmountBaseCurr: item.rate.value,
          DiscountAmount: 0,
          DiscountAmountBaseCurr: 0,
          PctTax: item.rate.pctIva ?? 0,
          TaxAmount: item.rate.ivaAmount ?? 0,
          TaxAmountBaseCurr: item.rate.iva,
          TotalAmount:
            (item.rate.valueAmount ?? 0) + (item.rate.ivaAmount ?? 0),
          TotalAmountBaseCurr: item.rate.value + item.rate.iva,
          CreationUser: user?.userLogin,
          CreationDate: new Date(),
          UpdateUser: user?.userLogin,
          StatusID: 1,
        });
      } else {
        acc.set(item.id, {
          ...documentItem,
          Quantity: documentItem.Quantity + 1,
          GrossAmount: documentItem.GrossAmount + (item.rate.valueAmount ?? 0),
          GrossAmountBaseCurr:
            documentItem.GrossAmountBaseCurr + item.rate.value,
          PctTax: documentItem.PctTax + (item.rate.pctIva ?? 0),
          TaxAmount: documentItem.TaxAmount + (item.rate.ivaAmount ?? 0),
          TaxAmountBaseCurr: documentItem.TaxAmountBaseCurr + item.rate.iva,
          TotalAmount:
            documentItem.TotalAmount +
            (item.rate.valueAmount ?? 0) +
            (item.rate.ivaAmount ?? 0),
          TotalAmountBaseCurr:
            documentItem.TotalAmountBaseCurr + item.rate.value + item.rate.iva,
        });
      }

      return acc;
    }, new Map<string, DocumentItemInterface>())
    .forEach((item) => documentItems.push(item));

  const uri = `${baseURLAPI}/Document/CreateDocumentV3`;
  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const body = JSON.stringify({
    AccountBillToID: owner?.id,
    AccountCode: owner?.accountCode,
    AccountEmail: owner?.listAccountEmail[0]?.email,
    AccountFiscalNumber: owner?.identificationNumber,
    AccountLegalAddress: owner?.fiscalAddress,
    AccountName: owner?.accountFullName,
    // El `?.` del índice no es cosmético: `shipmentServices` construye
    // `accountBillTo` con `listAccountPhone: []`, así que el crash es alcanzable.
    AccountPhone: owner?.listAccountPhone[0]?.phoneNumber,
    AccountSiteID: accountSiteID,
    BalanceAmountBaseCurr: document.balanceAmount,
    BUCode: buCode,
    ChangeAmountBaseCurr: "0",
    CollectingReportID: null,
    CreationUser: user?.userLogin,
    CurrencyId: 1,
    DiscountAmountBaseCurr: "0",
    DocCancelReasonID: null,
    DocumentAffectedID: null,
    DocumentAffectedTypeCode: "",
    DocumentAppliedID: null,
    DocumentID: null,
    DocumentNumber: null,
    DocumentSerie: null,
    DocumentTypeID: "6",
    DocumentTypeName: "ODF",
    ExchangeRate: exchangeRate,
    FiscalControlNumber: document.fiscalControlNumber,
    GrossAmountBaseCurr: grossAmountBaseCurr,
    HasRetention: document.hasRetention,
    IGTFTaxAmountBaseCurr: "0",
    IsInternational: document.isInternational,
    IsSpecialBilling: false,
    Items: documentItems,
    MunicipalTaxAmountBaseCurr: "0",
    Observations: observations,
    OtherTaxAmountBaseCurr: "0",
    PaymentModeID: paymentMode,
    PctDiscount: "0",
    PctIGTFTax: "0",
    PctMunicipalTax: "0",
    PctOtherTax: "0",
    PctPostalTax: "8",
    PctTax: "16",
    PostalTaxAmountBaseCurr: postalTaxAmountBaseCurr,
    TaxAmountBaseCurr: taxAmountBaseCurr,
    TaxExemptAmountBaseCurr: taxExemptAmountBaseCurr,
    TaxNotExemptAmountBaseCurrency: taxNoExemptAmountBaseCurr,
    SentToProvider: false,
    SentToProviderDate: null,
    Shipment: document.shipments.map((s) => {
      const shipment = s as ShipmentInterface;
      const postalTax = shipment.items.reduce(
        (acc, item) => acc + item.rate.ipostel,
        0
      );
      const safeKeepingBs = shipment.items.find((item) => item.id === "20")
        ?.rate.value;

      return {
        ShipmentHeaderID: s.id,
        ShipmentNumber: s.shipmentNumber,
        TotalPostalTax: postalTax,
        TotalPostalTaxBaseCurr: postalTax,
        TotalSafeKeeping: currencyExchange(
          safeKeepingBs ? safeKeepingBs : 0,
          exchanges,
          "USD",
          "BS"
        ),
        TotalSafeKeepingBaseCurr: safeKeepingBs,
        TotalAmount: currencyExchange(shipment.total, exchanges, "USD", "BS"),
        TotalAmountBaseCurr: shipment.total,
        ExchangeRate: exchangeRate,
        ShipmentItems: shipment.items.map((item) => ({
          ItemID: item.id,
          ShipmentHeaderID: shipment.id,
          ShipmentDetailID: null,
          DocumentItemID: null,
          PctDiscount: 0,
          TaxAmount: item.rate.ivaAmount,
          TaxAmountBaseCurr: item.rate.iva,
          PostalTaxAmount: item.rate.ipostelAmount,
          PostalTaxAmountBaseCurr: item.rate.ipostel,
          DiscountAmount: 0,
          DiscountAmountBaseCurr: 0,
          TotalAmount: item.rate.valueAmount,
          TotalAmountBaseCurr: item.rate.value,
          PctPostalTax: item.rate.pctIpostel ?? 8,
          PctTax: item.rate.pctIva ?? 16,
          CreationUser: user?.userLogin,
          CreationDate: new Date(),
          UpdateDate: new Date(),
          StatusID: 1,
        })),
      };
    }),
    ShipmentNumbers: document.shipments.map((s) => s.shipmentNumber),
    StatusID: 11,
    TotalAmountBaseCurr: document.balanceAmount,
    TypeCancelRequestID: null,
    DocumentDate: new Date(),
    DocumentDueDate: new Date(),
    LastStatusDate: new Date(),
    RegistrationTime: new Date(),
    CreationDate: new Date(),
    UpdateDate: new Date(),
    UpdateUser: user?.userLogin,
    UrlDocument: null,
  });

  try {
    const response = await api.post(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });
    if (response.status === 201 || response.status === 200) {
      return response.data;
    }
  } catch (error) {
    return (error as any)?.response?.data;
  }

  return {
    message: "",
    model: null,
    didError: true,
    errorMessage: "Hubo un error al guardar el documento.",
  };
};

// Create Document
export const saveDocument = async (
  document: DocumentInterface,
  exchanges: CurrencyExchanges,
  buCode?: string,
  user?: UserInterface,
  accountBillTo?: AccountInterface,
  observations?: string,
  applicationID?: number,
  exchangeRate?: number
): Promise<ResponseInterface<{ id_solicitud: string; documentID: string }>> => {
  const shipments = document.shipments as ShipmentInterface[];

  if (shipments.length === 0) {
    return {
      message: "",
      model: null,
      didError: true,
      errorMessage: "No se encontró el envío",
    };
  }

  if (!document.isInternational) {
    return await saveNationalDocument(
      document,
      buCode,
      user,
      accountBillTo,
      observations,
      applicationID
    );
  } else {
    return await saveInternationalDocument(
      document,
      exchanges,
      buCode,
      user,
      accountBillTo,
      observations,
      exchangeRate
    );
  }
};

export const updateDocumentStatus = async (
  documentId: string,
  status: DocumentStatus,
  documentType?: DocumentType,
  user?: UserInterface,
  buCodeInvoicer?: string,
  previousDocumentTypeID?: DocumentType
): Promise<ResponseInterface<boolean>> => {
  const body = JSON.stringify({
    DocumentID: documentId,
    StatusID: status,
    DocumentTypeID: documentType,
    UpdateUser: user?.userLogin,
    UpdateDate: new Date().toISOString(),
    BUCodeInvoicer: buCodeInvoicer,
    PreviousDocumentTypeID: previousDocumentTypeID,
  });

  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const uri = `${baseURLAPI}/Document/Status`;

  try {
    const response = await api.put(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });

    return response.data;
  } catch (error) {
    return (error as any)?.response?.data;
  }
};

export const updateDocumentAccountBillTo = async (
  documentId: string,
  accountBillTo: AccountInterface,
  user?: UserInterface
): Promise<ResponseInterface<boolean>> => {
  // Recambiar titular sobrescribe `AccountLegalAddress` en el documento. Sin esta
  // guarda, elegir una cuenta sin dirección **borra** la que ya estaba bien.
  const billingCheck = validateBillingAccount(accountBillTo);
  if (billingCheck.error) {
    return {
      message: "",
      model: null,
      didError: true,
      errorMessage: billingCheck.message,
    };
  }

  const body = JSON.stringify({
    DocumentID: documentId,
    AccountBillToID: accountBillTo.id,
    AccountPhone: accountBillTo.listAccountPhone[0]?.phoneNumber,
    AccountName: accountBillTo.accountFullName,
    AccountEmail: accountBillTo.listAccountEmail[0]?.email,
    AccountFiscalNumber: accountBillTo.identificationNumber,
    AccountLegalAddress: accountBillTo.fiscalAddress,
    UpdateUser: user?.userLogin,
    UpdateDate: new Date().toISOString(),
  });

  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const uri = `${baseURLAPI}/Document/AccountBillTo`;

  try {
    const response = await api.put(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });

    return response.data;
  } catch (error) {
    return (error as any)?.response?.data;
  }
};

export const createReplacement = async (
  documentAffectedID: string,
  documentAffectedTypeCode: string,
  documentNumber: string,
  fiscalControlNumber: string,
  documentTypeID: DocumentType,
  statusID: DocumentStatus,
  buCode: string,

  accountBillToID: string,
  accountName: string,
  accountPhone: string,
  accountEmail: string,
  accountFiscalNumber: string,
  accountLegalAddress: string,

  targetAccountBillToID: string,
  targetAccountName: string,
  targetAccountEmail: string,
  targetAccountPhone: string,
  targetAccountFiscalNumber: string,
  targetAccountLegalAddress: string,

  user: UserInterface
): Promise<ResponseInterface<DocumentReplacementDTO>> => {
  /**
   * Sólo se valida el DESTINO, nunca el origen.
   *
   * Esta es la única herramienta que repara una factura ya emitida, y el caso de
   * uso principal es precisamente que el origen esté mal: `FiscalSustitutionModal`
   * pasa el origen como `?? ""`. Una guarda sobre el origen dejaría inservible el
   * arreglo justo cuando hace falta.
   */
  if (!targetAccountLegalAddress?.trim()) {
    return {
      message: "",
      model: null,
      didError: true,
      errorMessage: MISSING_FISCAL_ADDRESS_ERROR,
    };
  }

  const body = JSON.stringify({
    SubstitutionReason: "Susticion de datos fiscales",
    DocumentAffectedID: documentAffectedID,
    DocumentAffectedTypeCode: documentAffectedTypeCode,
    DocumentNumber: documentNumber,
    FiscalControlNumber: fiscalControlNumber,
    DocumentTypeID: documentTypeID,
    StatusID: statusID,
    CreationUser: user.userLogin,
    TargetDocumentTypeID: 4,
    AccountName: accountName,
    AccountPhone: accountPhone,
    AccountEmail: accountEmail,
    AccountBillToID: accountBillToID,
    AccountFiscalNumber: accountFiscalNumber,
    AccountLegalAddress: accountLegalAddress,

    TargetAccountBillToID: targetAccountBillToID,
    TargetAccountName: targetAccountName,
    TargetAccountFiscalNumber: targetAccountFiscalNumber,
    TargetAccountPhone: targetAccountPhone,
    TargetAccountLegalAddress: targetAccountLegalAddress,
    TargetAccountEmail: targetAccountEmail,
    BUCode: buCode,
  });

  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const uri = `${baseURLAPI}/Document/CreateReplacement`;

  try {
    const response = await api.post(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });

    return response.data;
  } catch (error) {
    const data = (error as any)?.response?.data;
    if (data && typeof data === "object" && "didError" in data) {
      return data;
    }
    // Without a readable backend response (network failure, timeout or
    // gateway HTML) the outcome is indeterminate: the substitution may have
    // completed
    return {
      message: "",
      didError: true,
      errorMessage:
        "No se pudo confirmar el resultado de la sustitución. Verifique el documento antes de reintentar.",
      model: null,
    };
  }
};

export const updateOperationStatus = async (
  dailyStoreOperationID: number,
  operationStatusID: DailyOperationStatus,
  updateUser: string
): Promise<ResponseInterface<boolean>> => {
  const body = JSON.stringify({
    DailyStoreOperationID: dailyStoreOperationID,
    OperationStatusID: operationStatusID,
    UpdateUser: updateUser,
    UpdateDate: new Date().toISOString(),
  });

  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const uri = `${baseURLAPI}/Document/OperationStatus`;

  try {
    const response = await api.put(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
      cancelToken: source.token,
    });

    return response.data;
  } catch (error) {
    return (error as any).response.data;
  }
};

export const getStoreDocuments = async (
  buCode: string
): Promise<ResponseInterface<StoreDocumentInterface[]>> => {
  const uri = `${baseURLAPI}/Document/StoreDocuments?BuCode=${buCode}`;

  try {
    const response = await api.get(uri);

    return response.data;
  } catch (error) {
    return (error as any).response.data;
  }
};

export const searchDocumentsFullText = async (
  buCodeInvoicer: string,
  queryStr?: string,
  statusIDList?: DocumentStatus[],
  documentDateFrom?: string,
  documentDateTo?: string
): Promise<ResponseInterface<StoreDocumentInterface[]>> => {
  const uri = `${baseURLAPI}/Document/SearchDocumentsFullText`;

  const body = JSON.stringify({
    buCodeInvoicer,
    queryStr,
    statusIDList,
    documentDateFrom,
    documentDateTo,
  });

  try {
    const response = await api.post(uri, body, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    return (error as any).response.data;
  }
};

export const getBankIdByCode = (code: string): number => {
  const foundBank = banks.find((bank) => bank.BankCode === code);
  return foundBank ? foundBank.BankID : 0;
};

export const getBankNameByCode = (code: string): string => {
  const foundBank = banks.find((bank) => bank.BankCode === code);
  return foundBank ? foundBank.BankName : "Banco desconocido";
};

